import React from 'react';
import {View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {Button} from '../../shared/components/button';
import {Container} from '../../shared/components/container';
import {NewsCarousel} from '../../shared/components/newsCarousel';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {Drawer} from '../../shared/components/drawer';
import {useStyles} from '../../shared/hooks/styles';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {NewsResponsePayload} from '../../shared/models/api';
import {isNewsFilterCollapsedSelector, isFilterModalVisibleSelector} from '../../redux/selectors/runtime/news';
import {DeviceSize} from '../../shared/theme/helpers';
import {AllNews} from './components/allNews';
import {setFilterModalVisible} from '../../redux/actions/runtime/news';
import {useOnClickOutside} from '../../shared/hooks/useOutsideClick';
import {Filter} from './components/filter';

interface Props {
  latestNews: NewsResponsePayload[] | null;
}

export default (props: Props) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const isFilterButtonVisible = useSelector(isNewsFilterCollapsedSelector());
  const isFilterModalVisible = useSelector(isFilterModalVisibleSelector());
  const filterPopupRef = React.useRef<View>(null);
  const memoAllNews = React.useMemo(() => <AllNews />, []);
  const memoNewsCarousel = React.useMemo(() => <NewsCarousel news={props.latestNews} />, [props.latestNews]);

  const styles = useStyles(({theme, mediaExclude, mediaOnly}) => ({
    container: {
      backgroundColor: theme.color.backgroundMenuColor,
    },
    section: {
      ...mediaExclude(DeviceSize.desktop, {
        paddingHorizontal: 0,
      }),
    },
    sectionLatestNews: {
      ...mediaOnly(DeviceSize.desktop, {
        paddingBottom: 22,
      }),
    },
    newsContainer: {
      width: '100%',
      marginHorizontal: 'auto',
      backgroundColor: theme.color.grayMenu,
    },
    titleContainer: {
      ...mediaOnly(
        DeviceSize.desktop,
        {},
        {
          textAlign: 'center',
          paddingHorizontal: 16,
          alignItems: 'center',
        },
      ),
      flexDirection: 'row',
      justifyContent: 'space-between',
      ...mediaOnly(DeviceSize.desktop, {paddingTop: 40}, {paddingTop: 36}),
      zIndex: isFilterModalVisible ? 1 : 0,
    },
    title: {
      ...mediaOnly(DeviceSize.desktop, {
        alignSelf: 'flex-start',
      }),
      ...theme.fonts[isDesktop ? 'h2' : 'h6'],
    },
    sectionNews: {
      ...mediaExclude(DeviceSize.desktop, {
        maxWidth: 400,
      }),
      paddingBottom: 80,
    },
    titleLast: {
      ...mediaOnly(DeviceSize.desktop, {
        marginBottom: 12,
      }),
    },
    titleAll: {
      ...mediaOnly(DeviceSize.desktop, {
        marginBottom: 30,
      }),
    },
    filterContainer: {
      position: 'absolute',
      right: 0,
      top: 100,
    },
    filterModal: {
      ...mediaExclude(DeviceSize.desktop, {
        margin: 0,
        alignItems: 'flex-end',
      }),
    },
    filterModalInner: {
      ...mediaExclude(DeviceSize.desktop, {
        minHeight: '100%',
        backgroundColor: theme.color.blue100,
      }),
    },
    filter: {
      ...mediaExclude(DeviceSize.desktop, {
        elevation: 0,
        shadowOpacity: 0,
      }),
    },
    filterButton: {
      margin: 0,
    },
  }));

  React.useEffect(() => {
    if (!isFilterButtonVisible) {
      dispatch(setFilterModalVisible(false));
    }
  }, [isFilterButtonVisible]);

  useOnClickOutside(filterPopupRef, (event) => {
    const datePicker = document.querySelector('.MuiPickersBasePicker-container');

    if (datePicker && datePicker.contains(event.target as any)) {
      return;
    }
    dispatch(setFilterModalVisible(false));
  });

  return (
    <Screen style={styles.container} fullWidth>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <Container style={[styles.section, styles.sectionLatestNews]}>
          <Text
            testID='last-news-label-id'
            style={[styles.title, styles.titleContainer, styles.titleLast]}
            accessibilityRole='header'>
            Последние новости
          </Text>
          {memoNewsCarousel}
        </Container>

        <View style={styles.newsContainer}>
          <Container style={[styles.section, styles.sectionNews]}>
            <View style={styles.titleContainer}>
              <Text testID='all-news-label-id' style={[styles.title, styles.titleAll]} accessibilityRole='header'>
                Все новости
              </Text>
              {isFilterButtonVisible && (
                <Button
                  id='news-filter'
                  type='secondary'
                  text='Фильтр'
                  style={styles.filterButton}
                  onPress={() => dispatch(setFilterModalVisible(!isFilterModalVisible))}
                />
              )}
              {isDesktop && isFilterModalVisible && (
                <View style={[styles.filterContainer]} ref={filterPopupRef}>
                  <Filter />
                </View>
              )}
              {isDesktop || (
                <Drawer
                  style={styles.filterModal}
                  open={isFilterModalVisible}
                  onClose={() => dispatch(setFilterModalVisible(false))}>
                  <View style={styles.filterModalInner}>
                    <Filter style={styles.filter} closeButton />
                  </View>
                </Drawer>
              )}
            </View>
            {memoAllNews}
          </Container>
        </View>
      </CustomScrollView>
    </Screen>
  );
};
